import type { AccountGroup } from '$lib/accountGroup';
import type { ConstructorError } from '$lib/error';
import {
  Amount,
  companySchema,
  nameSchema,
  numberToDateSchema,
  Percent,
  recordIdSchema,
  Time,
  voucherIdSchema,
  type CorporateId,
  type ObjectMap,
  type VoucherId,
} from '@df/common';
import type { Result } from 'neverthrow';
import type { DndEvent } from 'svelte-dnd-action';
import { z } from 'zod';
import { zodDecoder } from '$lib/services/api/zod_decoder';
import type { AccountingDocumentType } from './document.type';
import { fileIdSchema } from './file_id.type';
import { materialityBenchmarksSchema } from './materiality.type';
import {
  accountGroupSchema,
  accountReferenceSchema,
  basAccountSchema,
  bookYearSchema,
  mapFromRecord,
  objectMapFromRecord,
  VoucherIdType,
} from './schemas';
import { voucherEntryIdSchema } from './schemas/voucher_entry_id';
import type { AccountGroupCategory } from '$lib/codegen/audit_account_groups';

type ValidName = { type: 'valid'; text: string };
type WarningName = { type: 'warning'; text: string; warningText: string };
export type ValidBookYear = { type: 'valid'; year: Time.BookYear };
export type ValidCorporateId = { type: 'valid'; id: CorporateId };
export type InvalidAccounting = { type: 'invalid'; text: string; invalidText: string };
type ValidAccountingType = { type: 'valid'; accountingType: AccountingDocumentType };
type InvalidAccountingType = { type: 'invalid'; accountingType: string };

export interface AccountingMetadata {
  companyName: ValidName | WarningName;
  corporateId: ValidCorporateId | InvalidAccounting;
  bookYear: ValidBookYear | InvalidAccounting;
  accountingType: ValidAccountingType | InvalidAccountingType;
}

export function getBookYear(bky: AccountingMetadata['bookYear']): string {
  return bky.type === 'valid' ? Time.BookYear.displayFull(bky.year) : bky.text;
}

export interface ValidAccountingMetadata {
  companyName: ValidName | WarningName;
  corporateId: ValidCorporateId;
  bookYear: ValidBookYear;
  accountingType: ValidAccountingType;
}

export type AccountReference = z.infer<typeof accountReferenceSchema>;
export const accountReferenceDecoder = zodDecoder(accountReferenceSchema);

export type BasAccount = z.infer<typeof basAccountSchema>;

export interface EventObject {
  event: CustomEvent<DndEvent>;
  group: AccountGroup;
}

const sniCodeSchema = z.object({
  code: z.number(),
  text: z.string(),
});

const accountInfoSchema = z.object({
  number: z.string(),
  name: z.string(),
  numOccurrences: z.number(),
  openingBalance: Amount.schema,
  closingBalance: Amount.schema,
});
export type AccountInfo = z.infer<typeof accountInfoSchema>;

const entrySchema = z.object({
  account: z.string(),
  amount: Amount.schema,
  date: z.coerce.date(),
  text: z.string(),
  id: z.string(),
});
export type Entry = z.infer<typeof entrySchema>;

const voucherSchema = z.object({
  voucherId: voucherIdSchema,
  date: z.coerce.date(),
  text: z.string(),
  totalDebit: Amount.schema,
  totalCredit: Amount.schema,
  entries: z.array(entrySchema),
});
export type Voucher = z.infer<typeof voucherSchema>;
export type VoucherConfiguration = {
  voucherId: VoucherIdType;
  date: Date;
  text: string;
  totalDebit: Amount;
  totalCredit: Amount;
  entries: Entry[];
};
export function transformVoucherConfiguration(
  voucher: Voucher,
  map: VoucherIdMap,
): Result<VoucherConfiguration, ConstructorError> {
  const id = VoucherIdType.fromId(voucher.voucherId, map);

  return id.map((id) => ({
    ...voucher,
    voucherId: id,
  }));
}

const accountVouchersSchema = mapFromRecord(z.string(), z.array(voucherIdSchema)); // key = AccountReference

const voucherNameSchema = z.object({
  series: z.string(),
  number: z.string(),
});
export type VoucherName = z.infer<typeof voucherNameSchema>;

const voucherIdMapSchema = mapFromRecord(voucherIdSchema, voucherNameSchema);
export type VoucherIdMap = z.infer<typeof voucherIdMapSchema>;
export const voucherIdMapDecoder = zodDecoder(voucherIdMapSchema);

const ledgerSchema = z.object({
  bookYear: bookYearSchema,
  company: companySchema,
  sniCodes: z.array(sniCodeSchema),
  accounts: mapFromRecord(z.string(), accountInfoSchema),
  vouchers: mapFromRecord(voucherIdSchema, voucherSchema),
  voucherIdMap: mapFromRecord(voucherIdSchema, voucherNameSchema),
  software: z.string().optional(),
  generatedAt: z.coerce.date().optional(),

  // Available Rust field not used
  // - units: Units
});

export const keyFigureKeys = [
  'grossProfit',
  'grossProfitMargin',
  'ebitda',
  'ebitdaMargin',
  'operatingProfit',
  'operatingProfitMargin',
  'workingCapital',
  'netCash',
  'solidityMargin',
  'costOfLaborMargin',
  'quickRatioMargin',
  'inventoryTurnover',
] as const;
export type KeyFigureKeys = (typeof keyFigureKeys)[number];
export function isKeyFigureKey(arg: unknown): arg is KeyFigureKeys {
  return typeof arg === 'string' && keyFigureKeys.includes(arg as KeyFigureKeys);
}

const accountingPeriodSchema = z.union([Time.YearMonth.schema, Time.BookYear.schema]);
export type AccountingPeriod = z.infer<typeof accountingPeriodSchema>;
export type AccountingPeriods = Time.YearMonth[] | Time.BookYear[];

const keyFigureValueSchema = <UnitSchema extends z.ZodType>(unitSchema: UnitSchema) =>
  z.object({
    accumulatedValue: unitSchema,
    periodValue: unitSchema,
  });
export type KeyFigureValue<TUnit> = {
  accumulatedValue: TUnit;
  periodValue: TUnit;
};

export type UnitType = Percent | Amount;
const keyFigureSchema = <UnitSchema extends z.ZodType>(unitSchema: UnitSchema) =>
  z.object({
    first: unitSchema,
    periods: mapFromRecord(accountingPeriodSchema, keyFigureValueSchema(unitSchema)),
  });

const keyFiguresSchema = z.object({
  totalAssets: keyFigureSchema(Amount.schema),
  grossProfit: keyFigureSchema(Amount.schema),
  grossProfitMargin: keyFigureSchema(Percent.schema),
  ebitda: keyFigureSchema(Amount.schema),
  ebitdaMargin: keyFigureSchema(Percent.schema),
  operatingProfit: keyFigureSchema(Amount.schema),
  operatingProfitMargin: keyFigureSchema(Percent.schema),
  workingCapital: keyFigureSchema(Amount.schema),
  netCash: keyFigureSchema(Amount.schema),
  unbookedResult: keyFigureSchema(Amount.schema),
  equity: keyFigureSchema(Amount.schema),
  solidityMargin: keyFigureSchema(Percent.schema),
  ownerSalary: keyFigureSchema(Amount.schema),
  revenue: keyFigureSchema(Amount.schema),
  costOfLaborMargin: keyFigureSchema(Percent.schema),
  quickRatioMargin: keyFigureSchema(Percent.schema),
  inventoryTurnover: keyFigureSchema(Percent.schema),
});
export type KeyFigures = z.infer<typeof keyFiguresSchema>;

// The key figures whose value is a Percent rather than an Amount — derived from
// each field's unit schema so it can't drift out of sync with keyFiguresSchema.
// Both units decode to branded numbers, so the key is the only discriminator.
const percentKeyFigures = new Set<KeyFigureKeys>(
  keyFigureKeys.filter((key) => keyFiguresSchema.shape[key].shape.first === Percent.schema),
);
export function isPercentKeyFigure(key: KeyFigureKeys): boolean {
  return percentKeyFigures.has(key);
}

export type ClassifiedUnit =
  { type: 'Percent'; value: Percent } | { type: 'Amount'; value: Amount };

// Percent and Amount are both branded numbers, so a value alone can't be told
// apart at runtime — the key figure's key is the discriminator. The assertion is
// sound because `keyFiguresSchema` decodes each key with a fixed unit schema.
export function classifyKeyFigureValue(key: KeyFigureKeys, value: UnitType): ClassifiedUnit {
  return isPercentKeyFigure(key)
    ? { type: 'Percent', value: value as Percent }
    : { type: 'Amount', value: value as Amount };
}

const financialStatementBalancesSchema = z.object({
  closingBalance: mapFromRecord(z.string(), Amount.schema), // key = AccountReference
  groupTotals: objectMapFromRecord(accountGroupSchema, Amount.schema),
});

const accountBalanceSchema = z.object({
  openingBalance: Amount.schema,
  periodChange: mapFromRecord(accountingPeriodSchema, Amount.schema),
  periodClosingBalance: mapFromRecord(accountingPeriodSchema, Amount.schema),
  closingBalance: Amount.schema,
});
export type AccountBalance = z.infer<typeof accountBalanceSchema>;

export interface AccountBalanceWithNumber extends AccountBalance {
  number: AccountReference;
  name: string;
}

export type MergedAccountBalance = {
  openingBalance: Amount | null;
  closingBalance: Amount | null;
  periodChange: Map<AccountingPeriod, Amount>;
  periodClosingBalance: Map<AccountingPeriod, Amount>;
  number: AccountReference;
  name: string;
};

const accountBalanceDictSchema = mapFromRecord(z.string(), accountBalanceSchema);

const groupBalanceSchema = z.object({
  openingBalance: Amount.schema,
  periodChange: mapFromRecord(accountingPeriodSchema, Amount.schema),
  periodClosingBalance: mapFromRecord(accountingPeriodSchema, Amount.schema),
  closingBalance: Amount.schema,
});
export type GroupBalance = z.infer<typeof groupBalanceSchema>;

const groupBalanceDictSchema = objectMapFromRecord(accountGroupSchema, groupBalanceSchema);
export type GroupBalances = z.infer<typeof groupBalanceDictSchema>;

export type MultiYearOverviewData = {
  bookYears: Time.BookYear[];
  balances: Map<Time.BookYear, GroupBalances>;
  groups: ObjectMap<AccountGroup, MultiYearAccountData>;
};

export type GroupCategoryAmounts = Map<
  AccountGroupCategory,
  { groups: ObjectMap<AccountGroup, string[]>; total: Amount }
>;

type AccountDifferences = Map<AccountReference, BalanceDifference>;

export type MultiYearAccountDifferences = Map<Time.BookYear, AccountDifferences>;

export type MultiYearAccountData = {
  balances: Map<Time.BookYear, Map<AccountReference, AccountBalanceWithNumber>>;
  accounts: Map<AccountReference, string>;
  differences: MultiYearAccountDifferences;
};

export type BalanceDifference = {
  type: 'Valid' | 'Warning' | 'Error';
  title: string;
};

export type OpeningBalancesAccounts = {
  balances: Map<Time.BookYear, Map<AccountReference, AccountBalanceWithNumber>>;
  data: Map<AccountReference, string>;
  differences: Map<AccountReference, BalanceDifference>;
};

export type OpeningBalancesData = [
  AccountGroup,
  {
    closingBalance: Amount | undefined;
    openingBalance: Amount | undefined;
    difference: BalanceDifference | undefined;
    accounts: OpeningBalancesAccounts;
  },
];

const financialOverviewSchema = z.object({
  periods: z.array(accountingPeriodSchema),
  accountBalances: accountBalanceDictSchema, // key = AccountReference
  groupBalances: groupBalanceDictSchema, // key = AccountGroup
});

const periodicLedgerDataSchema = z.object({
  type: z.enum(['YearMonth', 'BookYear']),
  financialOverview: financialOverviewSchema,
  keyFigures: keyFiguresSchema,
  yearKeyFigures: keyFiguresSchema.optional(),
});
export type PeriodicLedgerData = z.infer<typeof periodicLedgerDataSchema>;

const allLedgerDataSchema = z.object({
  accountVouchers: accountVouchersSchema,
  groupAccounts: objectMapFromRecord(accountGroupSchema, z.array(accountReferenceSchema)),
  invalidAccounts: z.array(accountReferenceSchema),
  closingBalances: financialStatementBalancesSchema,
  periodicData: periodicLedgerDataSchema,
  benchmarks: materialityBenchmarksSchema,
  ledger: ledgerSchema,
});
export type AllLedgerData = z.infer<typeof allLedgerDataSchema>;
export type PeriodKey = AllLedgerData['periodicData']['type'];

// Everything is unknown except for the values we use
type AllLedgerDataRaw = {
  accountVouchers: unknown; // value = VoucherId
  groupAccounts: unknown;
  invalidAccounts: unknown;
  closingBalances: unknown;
  periodicData: unknown; // to big and we don't need it when the data is in raw format
  benchmarks: unknown;
  ledger: {
    bookYear: unknown;
    company: unknown;
    sni: unknown;
    accounts: unknown;
    vouchers: unknown;
    voucherIdMap: Record<string, VoucherName>;
  };
};
const allLedgerDataRawSchema = z.object({
  ledger: z.object({
    voucherIdMap: mapFromRecord(z.string(), voucherNameSchema),
  }),
});

export const bookYearLedgerSchema = mapFromRecord(Time.BookYear.schema, allLedgerDataSchema);
export type BookYearLedger = z.infer<typeof bookYearLedgerSchema>;
export const bookYearLedgerDecoder = zodDecoder(bookYearLedgerSchema);
export type BookYearLedgerRaw = Record<string, AllLedgerDataRaw>;
export const bookYearLedgerRawDecoder = zodDecoder(
  mapFromRecord(z.string(), allLedgerDataRawSchema),
);

const subLedgerErrorOrWarningTypesWithoutData = [
  'FailedWithUnknownReason',
  'InvalidBookYear',
  'InvalidCorporateId',
  'NoVouchers',
  'InvalidSni',
  'InvalidSieType',
  'TooManyCustomVoucherSeries',
  'TooManyVoucherEntries',
  'TooManyUnitsInTransaction',
  // Excel
  'UnknownExcelFormat',
  'ExpectedASingleExcelMetadataRow',
] as const;
const subLedgerErrorOrWarningTypesWithData = [
  'MissingObject',
  'MultipleBalances',
  'BalanceWithoutAccount',
  'MultipleAccountNames',
  'FileCouldNotBeParsedAsSie',
  'NonNumericAccount',
  'InconsistentAccountBalances',
  'InconsistentTransactionLines',
  'VoucherOutsideBookYear',
  'EntryOutsideBookYear',
  'InvalidGenerationDate',
  'InvalidObject',
  'InvalidObjectReference',
  'AddedEntriesAsOriginalInUpdate',
  'DuplicateVoucherId',
  'MissingReferencedAccount',
  'NoContentInExcelSheet',
  'ExcelContentDeserialization',
  'MissingExcelSheets',
  'ConflictingExcelSheets',
  'InvalidExcelDataRange',
  'MissingOpeningBalanceInBalanceAccount',
  'MissingOpeningBalanceInResultAccount',
  'SkippedSieRow',
  // Errors/warnings found when trying to merge two files
  'MissingEntriesInUpdate',
  'InvalidEntryInUpdate',
  'RemovedVoucher',
] as const;
export type SubLedgerErrorOrWarningTypeWithData =
  (typeof subLedgerErrorOrWarningTypesWithData)[number];

export function isErrorOrWarningTypeWithData(
  string: unknown,
): string is SubLedgerErrorOrWarningTypeWithData {
  return (
    typeof string === 'string' &&
    subLedgerErrorOrWarningTypesWithData.includes(string as SubLedgerErrorOrWarningTypeWithData)
  );
}

const _subLedgerErrorOrWarningTypes = [
  ...subLedgerErrorOrWarningTypesWithoutData,
  ...subLedgerErrorOrWarningTypesWithData,
] as const;
export type SubLedgerErrorOrWarningType = (typeof _subLedgerErrorOrWarningTypes)[number];

const subLedgerErrorOrWarningWithoutDataSchema = z.object({
  type: z.enum(subLedgerErrorOrWarningTypesWithoutData),
});

const unitsMergeWarningSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('ChangedDimentionName'),
    dim: z.string(),
  }),
  z.object({
    type: z.literal('InconsistentUnitInfo'),
    dim: z.string(),
    unit: z.string(),
  }),
  z.object({
    type: z.literal('InconsistentUnitBalance'),
    dim: z.string(),
    unit: z.string(),
    account: accountReferenceSchema,
  }),
  z.object({
    type: z.literal('InconsistentUnitName'),
    dim: z.string(),
    unit: z.string(),
    before: z.string(),
    after: z.string(),
  }),
]);
export type UnitsMergeWarning = z.infer<typeof unitsMergeWarningSchema>;

const subLedgerErrorOrWarningWithDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('MissingObject'),
    data: z.string(),
  }),
  z.object({
    type: z.literal('FileCouldNotBeParsedAsSie'),
    data: z.string(),
  }),
  z.object({
    type: z.literal('MultipleBalances'),
    data: z.object({
      account: accountReferenceSchema,
    }),
  }),
  z.object({
    type: z.literal('BalanceWithoutAccount'),
    data: z.object({
      account: accountReferenceSchema,
      balance: Amount.schema,
    }),
  }),
  z.object({
    type: z.literal('MultipleAccountNames'),
    data: z.object({
      account: accountReferenceSchema,
    }),
  }),
  z.object({
    type: z.literal('NonNumericAccount'),
    data: z.object({
      account: z.string(), // String
      context: z.string(), // String
    }),
  }),
  z.object({
    type: z.literal('InvalidGenerationDate'),
    data: z.object({
      date: z.string(),
    }),
  }),
  z.object({
    type: z.literal('InconsistentAccountBalances'),
    data: z.object({
      account: accountReferenceSchema,
      openingBalance: Amount.schema,
      closingBalance: Amount.schema,
      calculatedBalance: Amount.schema,
    }),
  }),
  z.object({
    type: z.literal('VoucherOutsideBookYear'),
    data: z.object({
      voucher: voucherIdSchema,
      date: z.coerce.date(),
    }),
  }),
  z.object({
    type: z.literal('EntryOutsideBookYear'),
    data: z.object({
      voucher: voucherIdSchema,
      date: z.coerce.date(),
    }),
  }),
  z.object({
    type: z.literal('InconsistentTransactionLines'),
    data: voucherIdSchema,
  }),
  z.object({
    type: z.literal('InvalidObject'),
    data: z.object({
      message: z.string(),
    }),
  }),
  z.object({
    type: z.literal('InvalidObjectReference'),
    data: z.object({
      voucher: voucherIdSchema,
      reference: z.string(),
    }),
  }),
  z.object({
    type: z.literal('MissingEntriesInUpdate'),
    data: z.string(), // string = VoucherEntryId
  }),
  z.object({
    type: z.literal('AddedEntriesAsOriginalInUpdate'),
    data: z.string(), // string = VoucherEntryId
  }),
  z.object({
    type: z.literal('InvalidEntryInUpdate'),
    data: voucherIdSchema,
  }),
  z.object({
    type: z.literal('RemovedVoucher'),
    data: voucherIdSchema,
  }),
  z.object({
    type: z.literal('DuplicateVoucherId'),
    data: voucherIdSchema,
  }),
  z.object({
    type: z.literal('MissingReferencedAccount'),
    data: z.object({
      account: accountReferenceSchema,
      voucher: voucherIdSchema,
    }),
  }),
  z.object({
    type: z.literal('NoContentInExcelSheet'),
    data: z.object({
      sheetName: z.string(),
    }),
  }),
  z.object({
    type: z.literal('ExcelContentDeserialization'),
    data: z.object({
      message: z.string(),
    }),
  }),
  z.object({
    type: z.literal('MissingExcelSheets'),
    data: z.object({
      names: z.array(z.string()),
    }),
  }),
  z.object({
    type: z.literal('ConflictingExcelSheets'),
    data: z.object({
      names: z.array(z.string()),
    }),
  }),
  z.object({
    type: z.literal('InvalidExcelDataRange'),
    data: z.object({
      numExpectedRows: z.number().optional(),
      numExpectedColumns: z.number(),
      numReceivedRows: z.number(),
      numReceivedColumns: z.number(),
    }),
  }),
  z.object({
    type: z.literal('MissingOpeningBalanceInBalanceAccount'),
    data: z.object({
      account: accountReferenceSchema,
    }),
  }),
  z.object({
    type: z.literal('MissingOpeningBalanceInResultAccount'),
    data: z.object({
      account: accountReferenceSchema,
    }),
  }),
  z.object({
    type: z.literal('SkippedSieRow'),
    data: z.object({
      row: z.number(),
    }),
  }),
]);
export type SubLedgerErrorOrWarningWithData = z.infer<typeof subLedgerErrorOrWarningWithDataSchema>;

export type SubLedgerErrorOrWarningData =
  | string
  | number
  | VoucherId
  | {
      account: string;
      balance: Amount;
      calculatedBalance: Amount;
    }
  | {
      voucher: VoucherId;
    }
  | {
      account: string;
      context: string;
    }
  | {
      account: string;
    }
  | {
      message: string;
    }
  | {
      date: string;
    }
  | {
      sheetName: string;
    }
  | {
      names: string[];
    }
  | {
      row: number;
    }
  | {
      numExpectedRows?: number;
      numExpectedColumns: number;
      numReceivedRows: number;
      numReceivedColumns: number;
    };

const subLedgerErrorOrWarningSchema = z.union([
  subLedgerErrorOrWarningWithoutDataSchema,
  subLedgerErrorOrWarningWithDataSchema,
]);
export type SubLedgerErrorOrWarning = z.infer<typeof subLedgerErrorOrWarningSchema>;

const subRecordErrorStateSchema = z.object({
  type: z.literal('BlockedByError'),
  errors: z.array(subLedgerErrorOrWarningSchema),
});

const subRecordWarningStateSchema = z.object({
  type: z.literal('PresentWithWarnings'),
  version: z.number(),
  numVouchers: z.number(),
  warnings: z.array(subLedgerErrorOrWarningSchema),
});

const subRecordPresentStateSchema = z.object({
  type: z.literal('Present'),
  version: z.number(),
  numVouchers: z.number(),
});

const subRecordStateSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Processing'),
  }),
  subRecordErrorStateSchema,
  subRecordPresentStateSchema,
  subRecordWarningStateSchema,
]);

const externalLedgerFileTypes = ['SieYearEndBalance', 'SIE4', 'ExcelYearEndBalance'] as const;
const externalLedgerFileTypeSchema = z.enum(externalLedgerFileTypes);
export type ExternalLedgerFileType = (typeof externalLedgerFileTypes)[number];

const subRecordVersionMetadataSchema = z.object({
  uploadedAt: numberToDateSchema,
  generatedAt: z.string().nullish(),
  software: z.string().nullish(),
  externalLedgerFileType: externalLedgerFileTypeSchema.nullish(),
  state: subRecordStateSchema,
  uploaderId: z.string(),
  uploadedBy: nameSchema,
  fileId: fileIdSchema.nullable(),
});
export type SubRecordVersionMetadata = z.infer<typeof subRecordVersionMetadataSchema>;

const subRecordMetadataSchema = z.object({
  recordId: recordIdSchema.nullable(),
  versions: z.array(subRecordVersionMetadataSchema),
});
export type SubRecordMetadata = z.infer<typeof subRecordMetadataSchema>;

export const ledgerMetadataSchema = mapFromRecord(Time.BookYear.schema, subRecordMetadataSchema); // key = bookYearString
export type LedgerMetadata = z.infer<typeof ledgerMetadataSchema>;
export const ledgerMetadataDecoder = zodDecoder(ledgerMetadataSchema);

const voucherChangesSchema = z.object({
  allEntries: z.array(entrySchema),
  previouslyRemovedEntries: z.array(z.string()), // string = VoucherEntryId
  removedEntries: z.array(z.string()), // string = VoucherEntryId
  addedEntries: z.array(z.string()), // string = VoucherEntryId
  dateOrTextChange: z.boolean(),
});

const ledgerChangesSchema = z.object({
  addedVouchers: z.array(voucherIdSchema),
  changedVouchers: z.array(z.tuple([voucherIdSchema, voucherChangesSchema])),
  addedAccounts: z.array(accountReferenceSchema),
  changedAccountBalances: z.array(z.tuple([accountReferenceSchema, Amount.schema])), // Amount is previous balance
  canceledVouchers: z.array(voucherIdSchema),
  warningsAddedEntries: z.array(voucherEntryIdSchema),
  warningsRemovedEntries: z.array(voucherEntryIdSchema),
  unitsAndDimensionsChanges: z.array(unitsMergeWarningSchema),
});
export type ExternalLedgerChanges = z.infer<typeof ledgerChangesSchema>;
export const ledgerChangesDecoder = zodDecoder(ledgerChangesSchema);

const suggestionStatusSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('Suggestion'),
  }),
  z.object({
    type: z.literal('Handled'),
    data: basAccountSchema,
  }),
]);

const mappingSuggestionSchema = z.object({
  alternatives: z.array(basAccountSchema),
  status: suggestionStatusSchema,
});
export type MappingSuggestion = z.infer<typeof mappingSuggestionSchema>;

export const accountMappingSuggestionsSchema = z.object({
  suggestions: mapFromRecord(z.string(), mappingSuggestionSchema), // key = AccountReference
});
export type AccountMappingSuggestions = z.infer<typeof accountMappingSuggestionsSchema>;
export const accountMappingSuggestionsDecoder = zodDecoder(accountMappingSuggestionsSchema);

export const accountMappingSchema = mapFromRecord(z.string(), basAccountSchema); // key = AccountReference
export type AccountMapping = z.infer<typeof accountMappingSchema>;
export const accountMappingDecoder = zodDecoder(accountMappingSchema);

export const allAccountReferenceMappingsSchema = z.object({
  accountMapping: mapFromRecord(Time.BookYear.schema, basAccountSchema),
  suggestions: mapFromRecord(Time.BookYear.schema, mappingSuggestionSchema),
});
export type AllAccountReferenceMappings = z.infer<typeof allAccountReferenceMappingsSchema>;
export const allAccountReferenceMappings = zodDecoder(allAccountReferenceMappingsSchema);

export type AccountMappingHorizon =
  | {
      type: 'SingleBookYear';
      data: Time.BookYear;
    }
  | { type: 'FromBookYear'; data: Time.BookYear };

/** Where one account is remapped, and how far forward the remapping reaches. */
export type AccountRemapping = {
  basAccount: BasAccount;
  horizon: AccountMappingHorizon;
};

/**
 * A batch of remappings, at most one per account — the sender settles which of several remappings
 * of the same account wins, so the batch never holds two that fight.
 */
export type AccountMappingUpdate = Map<AccountReference, AccountRemapping>;

export type SortingLedgerVouchersByKey =
  | { type: 'voucher'; order: 'desc' | 'asc' }
  | { type: 'text'; order: 'desc' | 'asc' | 'orig' }
  | { type: 'date'; order: 'desc' | 'asc' | 'orig' }
  | { type: 'amount'; order: 'desc' | 'asc' | 'orig' };
